// Dependency imports
import { useState, useEffect, useRef, forwardRef } from 'react';

// Local imports
import { Button, Modal } from 'components';
import GroupLabelIcon from '../../../../assets/images/groupLabelIcon.svg';
import Canvas from './Canvas';

// Styles import
import Styles from './AnnotationStyles.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import {
  useGetConfigurationLabels,
  useGetFrames,
  useReplicateFrameData,
  useUpdateFrameData
} from 'api/sdk';
import { setFrameIndex } from 'store/annotations/annotationSlice';
import ImageComponent from './ImageComponent';
import { Tooltip, TooltipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnack } from 'plugins/snack';
import clsx from 'clsx';

// Create a stable tooltip component that won't jump during scrolling
const StableTooltip = styled((props: TooltipProps) => <Tooltip {...props} />)(
  ({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
      boxShadow: theme.shadows[1],
      fontSize: 11
    },
    // Key fix: these settings prevent the tooltip from jumping
    [`& .MuiTooltip-popper`]: {
      zIndex: 1200
    }
  })
);

const AnnotationPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const frameIndex = useSelector(
    (state: RootState) => state.annotation.frameIndex
  );

  const [loading, setLoading] = useState<Boolean>(true);
  const annotationData: any = useSelector(
    (state: RootState) => state.annotation.annotationData
  );
  const currentConfigId: any = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const currentCameraId: any = useSelector(
    (state: RootState) => state.configV2.currentCameraId
  );
  const { data: getExistingLabels, refetch } =
    useGetConfigurationLabels(currentConfigId);

  const snack = useSnack();

  const { data: frames, refetch: getFrames }: any = useGetFrames(
    currentConfigId,
    currentCameraId
  );

  const { mutateAsync: updateFrameData } = useUpdateFrameData();

  const { mutateAsync: replicateFrameData } = useReplicateFrameData();

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          // Previous frame
          if (frameIndex !== 1) {
            e.preventDefault(); // Prevent page scrolling
            handleFrameChange(frameIndex - 1);
          }
          break;

        case 'ArrowRight':
          // Next frame
          if (frameIndex < frames?.data?.frames.length) {
            e.preventDefault();
            handleFrameChange(frameIndex + 1);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [frameIndex, currentConfigId, currentCameraId, annotationData]);

  const handleFrameChange = async (newFrameIndex: number) => {
    try {
      setLoading(true);
      console.log('updating frame Data with this', {
        configId: currentConfigId,
        cameraId: currentCameraId,
        frameIdx: frameIndex,
        data: {
          bounding_box: {
            frame_data: annotationData
          }
        }
      });
      await updateFrameData({
        configId: currentConfigId,
        cameraId: currentCameraId,
        frameIdx: frameIndex,
        data: {
          bounding_box: {
            frame_data: annotationData
          }
        }
      });
      await getFrames();
      dispatch(setFrameIndex(newFrameIndex));
      await refetch();
    } catch (error) {
      console.error('Error updating frame:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReplicateFrameData = async () => {
    try {
      console.log('replicating frame data with this', {
        configId: currentConfigId,
        cameraId: currentCameraId,
        frameIdx: frameIndex,
        data: {
          bounding_box: {
            frame_data: annotationData
          }
        }
      });
      await updateFrameData({
        configId: currentConfigId,
        cameraId: currentCameraId,
        frameIdx: frameIndex,
        data: {
          bounding_box: {
            frame_data: annotationData
          }
        }
      }).then(async response => {
        if (response.status === 200) {
          await replicateFrameData({
            configId: currentConfigId,
            cameraId: currentCameraId,
            frameIdx: frameIndex
          }).then(response => {
            if (response.status === 200) {
              snack({
                message: response?.data?.message,
                severity: 'success'
              });
            } else {
              snack({
                message: 'Something went wrong!',
                severity: 'error'
              });
            }
          });
        }
      });
    } catch {}
  };

  return (
    <div className="flex items-start justify-betweeen gap-4 w-full h-[65vh]">
      <div className={Styles.firstcontainer}>
        <div className={Styles.headingholder}>
          <h3 className={Styles.heading}>Total Images</h3>
          <h4 className={Styles.imagecount}>
            {frames?.data?.frames.length || 0}
          </h4>
        </div>
        <div className={Styles.headingholder}>
          <h3 className={Styles.secondheading}>Current Frame Index</h3>
          <h3 className={Styles.secondheading}>
            {frameIndex}/{frames?.data?.frames.length || 0}
          </h3>
        </div>
        <div
          className={clsx(
            Styles.framesholder,
            'scrollbar scrollbar-track-gray-100 scrollbar-thumb-[#102433] hover:scrollbar-thumb-[#1c3a4f] scrollbar-thumb-rounded-full scrollbar-track-rounded-full overflow-auto'
          )}
        >
          {frames &&
            frames.data &&
            frames.data.frames.map((frame: any, index: number) => {
              return (
                <ImageComponent
                  key={frame.frame_idx}
                  frame={frame}
                  frameIndex={frameIndex}
                  setLoading={setLoading}
                  boundingBoxData={frame.bounding_box}
                  currentCameraId={currentCameraId}
                  currentConfigId={currentConfigId}
                />
              );
            })}
        </div>
      </div>
      <div className={Styles.secondcontainer}>
        <Canvas
          getExistingLabels={getExistingLabels}
          setLoading={setLoading}
          loading={loading}
          handleReplicateFrameData={handleReplicateFrameData}
        />
      </div>
      <div className={Styles.thirdcontainer}>
        <div className={Styles.headingholder}>
          <h3 className={Styles.heading}>Labels</h3>
          <h4 className={Styles.secondheading}>Image: {frameIndex}</h4>
        </div>
        <div className={Styles.objectlablesholder}>
          {annotationData.length === 0 ? (
            <div className={Styles.placeholder}>
              <img
                src={GroupLabelIcon}
                alt="grouplabelicon"
                className={Styles.grouplabelicon}
              />
              <h3 className={Styles.imagetext}>
                Annotate Object to show Labels
              </h3>
            </div>
          ) : (
            <div className={Styles.annotationListHolder}>
              {annotationData.length > 0 &&
                annotationData.map((data: any, index: number) => {
                  return (
                    <div
                      className={Styles.annotationList}
                      key={data?.id}
                      style={{
                        border: `1px solid ${data?.color?.stroke || 'black'}`,
                        background: data?.color?.fill,
                        color: '#000'
                      }}
                    >
                      <h3 className={Styles.labelname}>{data?.label}</h3>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
        <div className={Styles.buttonholder}>
          <StableTooltip
            title="Previous (←)"
            placement="top"
            PopperProps={{
              disablePortal: true,
              popperOptions: {
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      altBoundary: true,
                      rootBoundary: 'document',
                      padding: 8
                    }
                  }
                ]
              }
            }}
            enterTouchDelay={0}
            leaveTouchDelay={0}
            enterDelay={200}
            leaveDelay={0}
            disableInteractive
          >
            <span style={{ display: 'inline-block' }}>
              <Button
                variant="outlined"
                version="light"
                onClick={() => {
                  if (frameIndex !== 1) {
                    handleFrameChange(frameIndex - 1);
                  }
                }}
                disabled={frameIndex === 1}
              >
                Previous
              </Button>
            </span>
          </StableTooltip>

          <StableTooltip
            title="Next (→)"
            placement="top"
            PopperProps={{
              disablePortal: true,
              popperOptions: {
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      altBoundary: true,
                      rootBoundary: 'document',
                      padding: 8
                    }
                  }
                ]
              }
            }}
            enterTouchDelay={0}
            leaveTouchDelay={0}
            enterDelay={200}
            leaveDelay={0}
            disableInteractive
          >
            <span style={{ display: 'inline-block' }}>
              <Button
                onClick={() => {
                  if (frameIndex <= frames?.data?.frames.length) {
                    handleFrameChange(frameIndex + 1);
                  }
                }}
                disabled={frameIndex >= frames?.data?.frames.length}
              >
                Next
              </Button>
            </span>
          </StableTooltip>
        </div>
      </div>
    </div>
  );
};

export default AnnotationPage;
